/**
 * HTTP Service Singleton
 * Centralized HTTP request handling for all Vue components.
 */

import { log } from '../utils/DebugLogger'
import { eventBus } from '../eventBus'
import { tokenService, tokensFromRefresh } from './tokenService'
import { config } from './configService'
import { useUnsavedWork } from '../composables/useUnsavedWork'
import * as Sentry from '@sentry/vue'
import type {
  InputContractRegistry,
  ResponseContractRegistry,
} from '../contracts/registry'

const sistemaHost = config.api.sistemaHost

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS'

export interface ApiResponse<TDados = unknown> {
  success: boolean
  dados?: TDados
  error?: string | null
}

export interface RequestOptions {
  method?: HttpMethod | string
  headers?: Record<string, string>
  body?: unknown
  params?: Record<string, string | number | boolean | null | undefined> | null
  timeout?: number
  skipJsonParse?: boolean
  skipErrorDisplay?: boolean
  skipLoading?: boolean
  logContext?: string
  isRetry?: boolean
  /**
   * Name of a registered Response contract. When set, the parsed JSON response
   * is validated against the contract's envelope + dados shape via
   * validateResponse(). Throws an HttpError with kind='response-shape-mismatch'
   * on drift. Per-call opt-in for incremental adoption.
   *
   * When this string is a key of `ResponseContractRegistry`, the typed
   * httpService overloads also use it to resolve the call's return type at
   * compile time (Go-client style) — see overloads below.
   */
  responseContract?: string
  /**
   * Name of a registered Input contract. When this string is a key of
   * `InputContractRegistry`, the typed `post` / `put` / `patch` overloads
   * resolve the request `data` parameter type from the registry at compile
   * time. At runtime, the payload is automatically validated against the
   * contract's field schema before the request is sent. An invalid payload
   * throws an HttpError with `kind === 'request-shape-mismatch'` without
   * making any network call. Callers no longer need to call `validate()`
   * manually when `inputContract` is set.
   */
  inputContract?: string
  /**
   * Opt-in refusal guard for the platform envelope.
   *
   * A REFUSAL from this backend arrives as HTTP **200** with
   * `{success:false, dados:null, error:'…'}` — `MY_Controller::sendError()`
   * never touches the status code. So `fetch` resolves, `!response.ok` is
   * false, nothing throws, and `validateResponse()` only checks the envelope
   * shape (it validates `dados` on success responses only). A call site that
   * reads `dados` and reports success therefore CELEBRATES an operation that
   * never happened — the bug class that has shipped four times (skill
   * billing-staff-console §4).
   *
   * When set, a `success === false` envelope rejects with
   * `Error(envelope.error ?? fallback)` carrying `kind === 'envelope-failure'`
   * — byte for byte what the composables that got this right write by hand.
   * Pass a string to keep a domain-specific fallback for the (rare) refusal
   * that carries no `error` text; `true` uses ENVELOPE_FAILURE_FALLBACK.
   *
   * DEFAULT OFF, per call site — this flag IS the rollback switch: removing it
   * from one call restores that call's previous behaviour, and a call site that
   * never sets it is not affected at all.
   *
   * The refusal deliberately does NOT go through `showError()`. The named
   * reason belongs to the caller, which already picks the tone (a `deferred`
   * cut is a warning, a `gateway_failed` cut is an error) and renders its own
   * message; a generic red toast from here would duplicate every one of them.
   */
  throwOnEnvelopeFailure?: boolean | string
}

/** Reason used when a refused envelope carries no `error` text. */
export const ENVELOPE_FAILURE_FALLBACK = 'requisicao_recusada'

export interface QueryOverrideParams {
  franquiaIds: Array<number | string>
  dataInicio: string
  dataFim: string
  userIds?: string[]
}

export interface HttpError extends Error {
  response?: Response
  status?: number
  statusText?: string
  data?: unknown
  /** Tags structured error categories. Used: 'response-shape-mismatch', 'request-shape-mismatch', 'envelope-failure'. */
  kind?: string
}

type TokenSubscriber = (token: string) => void

class HttpService {
  defaultTimeout: number = config.api.timeout
  defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
  pendingRequests: number = 0
  loadingDebounceTimer: ReturnType<typeof setTimeout> | null = null
  isRefreshing: boolean = false
  refreshSubscribers: TokenSubscriber[] = []

  subscribeTokenRefresh(callback: TokenSubscriber): void {
    this.refreshSubscribers.push(callback)
  }

  onTokenRefreshed(token: string): void {
    this.refreshSubscribers.forEach(callback => callback(token))
    this.refreshSubscribers = []
  }

  showLoadingIndicator(): void {
    this.pendingRequests++

    log('HttpService', 'showLoadingIndicator', 'Pending requests:', this.pendingRequests)

    if (this.pendingRequests === 1) {
      this.loadingDebounceTimer = setTimeout(() => {
        eventBus.emit('loading.show')
        log('HttpService', 'showLoadingIndicator', 'Loading indicator shown (debounce completed)')
      }, 200)
    }
  }

  hideLoadingIndicator(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1)

    log('HttpService', 'hideLoadingIndicator', 'Pending requests:', this.pendingRequests)

    if (this.pendingRequests === 0) {
      if (this.loadingDebounceTimer) clearTimeout(this.loadingDebounceTimer)
      eventBus.emit('loading.hide')
      log('HttpService', 'hideLoadingIndicator', 'Loading indicator hidden (all requests complete)')
    }
  }

  async request<T = unknown>(url: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body = null,
      params = null,
      timeout = this.defaultTimeout,
      skipJsonParse = false,
      skipErrorDisplay = false,
      skipLoading = false,
      logContext = 'HttpService'
    } = options

    if (params && typeof params === 'object') {
      const queryString = new URLSearchParams(params as Record<string, string>).toString()
      if (queryString) {
        url = url + (url.includes('?') ? '&' : '?') + queryString
      }
    }

    const authExcludedEndpoints = [
      '/loginApi/v1/user/logaUsuario',
      '/loginApi/v1/user/registraUsuario',
      '/loginApi/v1/user/refreshToken',
      '/loginApi/v1/user/esqueciMinhaSenha',
      '/loginApi/v1/user/confirmaEsqueciMinhaSenha',
      '/loginApi/v1/user/confirmaUsuario',
      '/loginApi/v1/user/reenviarConfirmacao'
    ]

    let urlPath = url
    try {
      const urlObj = new URL(url, window.location.origin)
      urlPath = urlObj.pathname + urlObj.search
    } catch {
      urlPath = url
    }

    const shouldExcludeAuth = authExcludedEndpoints.some(endpoint => urlPath.startsWith(endpoint))

    const requestConfig: RequestInit = {
      method: method.toUpperCase(),
      headers: {
        ...this.defaultHeaders,
        ...headers
      },
      credentials: 'include'
    }
    const finalHeaders = requestConfig.headers as Record<string, string>

    const accessToken = tokenService.getAccessToken()
    log(logContext, 'request', `🔍 Token check - hasToken: ${!!accessToken}, shouldExclude: ${shouldExcludeAuth}, url: ${url}, urlPath: ${urlPath}`)

    if (accessToken && !shouldExcludeAuth) {
      finalHeaders['Authorization'] = `Bearer ${accessToken}`
      log(logContext, 'request', '🔐 Added Authorization header (JWT)', { tokenPreview: accessToken.substring(0, 50) + '...' })
    } else if (!accessToken) {
      log(logContext, 'request', '⚠️ No access token available')
    } else if (shouldExcludeAuth) {
      log(logContext, 'request', '⚠️ Auth excluded for this endpoint')
    }

    if (body !== null) {
      if (typeof body === 'object' && !(body instanceof FormData)) {
        requestConfig.body = JSON.stringify(body)
      } else {
        requestConfig.body = body as BodyInit
        if (body instanceof FormData) {
          delete finalHeaders['Content-Type']
        }
      }
    }

    const controller = new AbortController()
    requestConfig.signal = controller.signal

    const timeoutId = setTimeout(() => {
      controller.abort()
    }, timeout)

    const startTime = Date.now()

    if (!skipLoading) {
      this.showLoadingIndicator()
    }

    log(logContext, 'request', `${(method as string).toUpperCase()} ${url}`, {
      headers: requestConfig.headers,
      body: requestConfig.body,
      timeout,
      skipLoading
    })

    // Optional runtime input-contract validation. When set, refuse to send a
    // payload that doesn't match the registered schema. Same opt-in semantics
    // as `responseContract`.
    //
    // FormData bodies are skipped: multipart uploads use PHP `source='upload'`
    // contracts that mirror `$_FILES` server-side. The frontend never sends a
    // `_files` key on the wire, so validating a FormData against that schema
    // would always fail. The backend's InputContract still validates the
    // upload after `$_FILES` injection.
    if (options.inputContract && body !== null && body !== undefined && !(body instanceof FormData)) {
      try {
        const { validate } = await import('../contracts/validate')
        // Internal-only runtime validation — the public typed overloads on
        // get/post/put/patch already constrain `options.inputContract` to a
        // known key, but at this lower layer we deliberately accept any
        // string and let `registry.get(...)` reject unknown names at runtime.
        // The double-cast bypasses the build-time fence on validate() that
        // public call sites rely on.
        type InputName = Parameters<typeof validate>[0]
        validate(options.inputContract as InputName, body as never)
      } catch (validationError) {
        const message = `Request shape mismatch [${options.inputContract}]: ${(validationError as Error).message}`
        console.error(`[HttpService] ${message}`, { url, body })
        const err = new Error(message) as HttpError
        err.kind = 'request-shape-mismatch'
        err.data = body
        throw err
      }
    }

    try {
      let finalUrl: string
      const cacheBust = (url.includes('?') ? '&' : '?') + 't=' + Date.now()
      if (url.startsWith('http://') || url.startsWith('https://')) {
        finalUrl = url + cacheBust
      } else {
        finalUrl = sistemaHost + url + cacheBust
      }

      const response = await fetch(finalUrl, requestConfig)
      clearTimeout(timeoutId)

      const duration = Date.now() - startTime

      log(logContext, 'response', `${response.status} ${response.statusText} (${duration}ms)`, {
        url,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        duration
      })

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`) as HttpError
        error.response = response
        error.status = response.status
        error.statusText = response.statusText

        try {
          const errorData = await response.json()
          error.data = errorData
          // `error` is the PLATFORM ENVELOPE's failure field — {success, dados,
          // error} — and it was missing from this chain, so every named refusal
          // the backend produces was discarded right here. The whole point of
          // codes like `mercado_nao_atende_pais` or `setup_fee_owed` is that a
          // human or a support script can see WHY; instead the user got
          // "HTTP 422: " with nothing after the colon, because HTTP/2 sends no
          // statusText and the reason had already been dropped.
          //
          // Appended rather than prepended: `message` and `Error` are what
          // non-envelope responses use (Go services, the auth layer), and
          // reordering could change a message that works today. This only fills
          // in where there was nothing.
          //
          // Typeguarded because `error` is a string in the envelope but some
          // upstreams send `error: true`, and "true" is a worse message than the
          // status line.
          //
          // A guarda vale para os TRÊS campos. `message` e `Error` ficaram de
          // fora e eram os únicos pontos do código que atribuíam um valor cru à
          // mensagem: um responder não-envelope que manda `message: 26` fazia a
          // tela exibir "Erro ao carregar dados: 26" — um número pelado, sem
          // motivo e sem rastro (chamado 2026-0009). O mesmo argumento que
          // justificou a guarda em `error` vale aqui: a linha de status é pior
          // que um motivo, mas é melhor que um valor que não quer dizer nada.
          const textoDeErro = (v: unknown): string | null =>
            typeof v === 'string' && v !== '' ? v : null

          // O valor descartado vai para o console: sem isto, um campo não
          // textual some sem deixar vestígio e a investigação recomeça do zero.
          const naoTextual = [errorData?.message, errorData?.Error, errorData?.error]
            .find(v => v !== undefined && v !== null && typeof v !== 'string')
          if (naoTextual !== undefined) {
            console.error('[HttpService] campo de erro não textual descartado', {
              url,
              status: response.status,
              valor: naoTextual,
              tipo: typeof naoTextual,
              response: errorData
            })
          }

          error.message = textoDeErro(errorData?.message)
            ?? textoDeErro(errorData?.Error)
            ?? textoDeErro(errorData?.error)
            ?? error.message
        } catch {
          try {
            error.data = await response.text()
          } catch {
            // Ignore text parsing errors
          }
        }

        // F10 (D5 FE half + M-UNSAVED) — subscription / setup-fee gate: PHP 403 + subscription_blocked | setup_fee_owed →
        // redirect to /pagamento-e-uso (the recovery view).
        // Before navigating, check for in-progress work: dirty → confirm dialog; clean →
        // immediate redirect. Either way the error is thrown so the caller surfaces the 403.
        // The interceptor is the recovery boundary because router.beforeEach deliberately
        // proceeds on a permissions-load failure (server-side scoping is authoritative).
        const errCode = (error.data as { error?: string })?.error
        // implantacao_em_andamento (chamado NS 2026-0079): franquia em implantação sem contrato
        // aprovado e taxa paga — a recuperação é a jornada dela, não a tela de pagamento.
        const bloqueio = errCode === 'subscription_blocked' || errCode === 'setup_fee_owed' || errCode === 'implantacao_em_andamento'
        if (response.status === 403 && bloqueio && !options.isRetry) {
          const proceed = useUnsavedWork().hasDirty()
            ? window.confirm('Você tem trabalho não salvo. Deseja continuar e perder as alterações?')
            : true
          if (proceed) {
            window.location.href = errCode === 'implantacao_em_andamento' ? '/meu-onboarding' : '/pagamento-e-uso'
          }
          throw error
        }


        if (response.status === 401 && !shouldExcludeAuth && !options.isRetry) {
          log(logContext, 'error', '🔄 401 Unauthorized - attempting token refresh and retry')

          if (this.isRefreshing) {
            log(logContext, 'error', '⏳ Refresh already in progress - queuing request')

            return new Promise<T>((resolve, reject) => {
              this.subscribeTokenRefresh(() => {
                log(logContext, 'error', '✅ Token refreshed - retrying request')

                this.request<T>(url, { ...options, isRetry: true })
                  .then(resolve)
                  .catch(reject)
              })
            })
          }

          this.isRefreshing = true

          try {
            const refreshUrl = config.api.endpoints.cognito.refreshToken
            log(logContext, 'error', `🔄 Attempting token refresh at: ${refreshUrl}`)

            const refreshResponse = await fetch(refreshUrl, {
              method: 'POST',
              credentials: 'include'
            })

            if (refreshResponse.ok) {
              const tokens = tokensFromRefresh(await refreshResponse.json())

              if (tokens) {
                log(logContext, 'error', '✅ Token refresh successful')

                tokenService.setTokens(tokens.accessToken, tokens.idToken)
                this.onTokenRefreshed(tokens.accessToken)

                this.isRefreshing = false

                return this.request<T>(url, { ...options, isRetry: true })
              }
            }

            log(logContext, 'error', '❌ Token refresh failed - redirecting to login')
            this.isRefreshing = false

            tokenService.clearTokens()
            window.location.href = `/login?redirectTo=${encodeURIComponent(window.location.pathname)}`

            throw error
          } catch (refreshError) {
            log(logContext, 'error', '❌ Token refresh error:', refreshError)
            this.isRefreshing = false

            tokenService.clearTokens()
            window.location.href = `/login?redirectTo=${encodeURIComponent(window.location.pathname)}`

            throw error
          }
        }

        throw error
      }

      let responseData: unknown

      if (skipJsonParse) {
        responseData = response
      } else {
        const responseText = await response.text()

        try {
          responseData = JSON.parse(responseText)
        } catch (jsonError) {
          throw new Error(`Invalid JSON response: ${(jsonError as Error).message}. Response: ${responseText.substring(0, 200)}`)
        }
      }

      // Optional runtime response-contract validation
      if (!skipJsonParse && options.responseContract) {
        try {
          const { validateResponse } = await import('../contracts/validate')
          validateResponse(options.responseContract, responseData)
        } catch (validationError) {
          const message = `Response contract violation [${options.responseContract}]: ${(validationError as Error).message}`
          // Surface violations in the browser console (and therefore in the
          // Playwright cons.err capture). Without this, the only visible
          // signal is the user-facing toast, which truncates the message
          // and is invisible to the tracer.
          console.error(`[HttpService] ${message}`, { url, response: responseData })
          const err = new Error(message) as HttpError
          err.kind = 'response-shape-mismatch'
          err.data = responseData
          log(logContext, 'response', `❌ Response contract violation [${options.responseContract}]`, {
            url,
            error: (validationError as Error).message
          })
          throw err
        }
      }

      // Optional envelope-refusal guard (opt-in — see throwOnEnvelopeFailure).
      // Runs AFTER the contract check so a malformed envelope still reports as
      // a shape mismatch, and BEFORE the success path so a refused write can
      // never reach a caller that would treat it as done.
      if (!skipJsonParse && options.throwOnEnvelopeFailure) {
        const envelope = responseData as ApiResponse | null
        if (envelope !== null && typeof envelope === 'object' && envelope.success === false) {
          const fallback = typeof options.throwOnEnvelopeFailure === 'string'
            ? options.throwOnEnvelopeFailure
            : ENVELOPE_FAILURE_FALLBACK
          const err = new Error(envelope.error ?? fallback) as HttpError
          err.kind = 'envelope-failure'
          err.data = responseData
          log(logContext, 'response', '⛔ Envelope refusal (HTTP 200)', {
            url,
            error: envelope.error,
          })
          throw err
        }
      }

      log(logContext, 'response', 'Response data received', {
        dataType: typeof responseData,
        dataSize: responseData ? JSON.stringify(responseData).length : 0
      })

      if (!skipLoading) {
        this.hideLoadingIndicator()
      }

      return responseData as T

    } catch (rawError) {
      const error = rawError as HttpError
      clearTimeout(timeoutId)
      const duration = Date.now() - startTime

      if (!skipLoading) {
        this.hideLoadingIndicator()
      }

      let errorMessage = error.message
      const errorDetails: Record<string, unknown> = { url, method, duration }

      if ((error as Error).name === 'AbortError') {
        errorMessage = `Request timeout after ${timeout}ms`
        errorDetails.timeout = true
      } else if (error.response) {
        errorDetails.status = error.status
        errorDetails.statusText = error.statusText
        errorDetails.data = error.data
      } else if ((error as Error).name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error - please check your connection'
        errorDetails.networkError = true
      }

      log(logContext, 'error', `Request failed: ${errorMessage}`, errorDetails)

      const status = error.status || 0
      if (status >= 500 || errorDetails.networkError || errorDetails.timeout) {
        Sentry.captureException(error, {
          level: status >= 500 ? 'error' : 'warning',
          tags: {
            http_status: String(status),
            http_method: (method as string).toUpperCase(),
            endpoint: url,
            error_type: errorDetails.timeout ? 'timeout' :
                       errorDetails.networkError ? 'network' : 'http_error'
          },
          extra: {
            duration,
            errorDetails,
            requestBody: body,
            responseData: error.data
          },
          contexts: {
            http: {
              url: sistemaHost + url,
              method: (method as string).toUpperCase(),
              status_code: status,
              data: errorDetails
            }
          }
        })
      }

      // An envelope refusal is a DOMAIN answer, not a transport failure: the
      // caller owns the message and its tone (see throwOnEnvelopeFailure).
      // Toasting it here would double every message the consoles already show.
      if (!skipErrorDisplay && error.kind !== 'envelope-failure') {
        this.showError(errorMessage, error)
      }

      throw error
    }
  }

  // ─── get ────────────────────────────────────────────────────────────────
  // Typed overloads come BEFORE the fallback so TS picks them when the call
  // site supplies `responseContract`. The overload list is part of the public
  // type surface — implementation signature stays at the bottom.
  get<R extends keyof ResponseContractRegistry>(
    url: string,
    options: RequestOptions & { responseContract: R }
  ): Promise<ResponseContractRegistry[R]>
  get<T = unknown>(url: string, options?: RequestOptions): Promise<T>
  async get(url: string, options: RequestOptions = {}): Promise<any> {
    return this.request(url, { ...options, method: 'GET' })
  }

  // ─── post ───────────────────────────────────────────────────────────────
  post<I extends keyof InputContractRegistry, R extends keyof ResponseContractRegistry>(
    url: string,
    data: InputContractRegistry[I],
    options: RequestOptions & { inputContract: I; responseContract: R }
  ): Promise<ResponseContractRegistry[R]>
  post<R extends keyof ResponseContractRegistry>(
    url: string,
    data: unknown,
    options: RequestOptions & { responseContract: R }
  ): Promise<ResponseContractRegistry[R]>
  post<I extends keyof InputContractRegistry>(
    url: string,
    data: InputContractRegistry[I],
    options: RequestOptions & { inputContract: I }
  ): Promise<unknown>
  post<T = unknown>(url: string, data?: unknown, options?: RequestOptions): Promise<T>
  async post(url: string, data: unknown = null, options: RequestOptions = {}): Promise<any> {
    return this.request(url, { ...options, method: 'POST', body: data })
  }

  // ─── put ────────────────────────────────────────────────────────────────
  put<I extends keyof InputContractRegistry, R extends keyof ResponseContractRegistry>(
    url: string,
    data: InputContractRegistry[I],
    options: RequestOptions & { inputContract: I; responseContract: R }
  ): Promise<ResponseContractRegistry[R]>
  put<R extends keyof ResponseContractRegistry>(
    url: string,
    data: unknown,
    options: RequestOptions & { responseContract: R }
  ): Promise<ResponseContractRegistry[R]>
  put<I extends keyof InputContractRegistry>(
    url: string,
    data: InputContractRegistry[I],
    options: RequestOptions & { inputContract: I }
  ): Promise<unknown>
  put<T = unknown>(url: string, data?: unknown, options?: RequestOptions): Promise<T>
  async put(url: string, data: unknown = null, options: RequestOptions = {}): Promise<any> {
    return this.request(url, { ...options, method: 'PUT', body: data })
  }

  // ─── delete ─────────────────────────────────────────────────────────────
  delete<R extends keyof ResponseContractRegistry>(
    url: string,
    options: RequestOptions & { responseContract: R }
  ): Promise<ResponseContractRegistry[R]>
  delete<T = unknown>(url: string, options?: RequestOptions): Promise<T>
  async delete(url: string, options: RequestOptions = {}): Promise<any> {
    return this.request(url, { ...options, method: 'DELETE' })
  }

  // ─── patch ──────────────────────────────────────────────────────────────
  patch<I extends keyof InputContractRegistry, R extends keyof ResponseContractRegistry>(
    url: string,
    data: InputContractRegistry[I],
    options: RequestOptions & { inputContract: I; responseContract: R }
  ): Promise<ResponseContractRegistry[R]>
  patch<R extends keyof ResponseContractRegistry>(
    url: string,
    data: unknown,
    options: RequestOptions & { responseContract: R }
  ): Promise<ResponseContractRegistry[R]>
  patch<I extends keyof InputContractRegistry>(
    url: string,
    data: InputContractRegistry[I],
    options: RequestOptions & { inputContract: I }
  ): Promise<unknown>
  patch<T = unknown>(url: string, data?: unknown, options?: RequestOptions): Promise<T>
  async patch(url: string, data: unknown = null, options: RequestOptions = {}): Promise<any> {
    return this.request(url, { ...options, method: 'PATCH', body: data })
  }

  /**
   * POST a JSON body and yield decoded text chunks from the response body stream
   * (for Server-Sent Events / text/event-stream responses).
   *
   * This is the ONLY legal raw-stream site — the no-raw-axios rule exempts files
   * whose basename starts with `httpService`. All composables/components consume
   * this method; they must never call fetch/getReader directly.
   *
   * Key decisions vs the JSON path:
   *  - `skipLoading: true` — the composable owns `loading`; do NOT pin the global
   *    spinner for a potentially long LLM stream.
   *  - No short AbortController timeout — a long stream would otherwise abort
   *    mid-token. We use a very large sentinel (24 h) instead of 0 so the
   *    abort signal still exists for framework compatibility.
   *  - No 401 refresh-and-replay — replaying a half-consumed stream is wrong.
   *    A 401 (thrown by the !response.ok path before any frame) surfaces as an
   *    HttpError with `.status === 401`, matching the PT session-expired mapping
   *    in the composable exactly as before.
   *  - `response.body` null is a typed error (never `any`).
   */
  async *postStream(
    url: string,
    body: unknown,
    options: Omit<RequestOptions, 'method' | 'body' | 'skipJsonParse'> = {},
  ): AsyncIterable<string> {
    // Reuse request() with skipJsonParse so we get the raw Response object back.
    // The very-large timeout (24 h) avoids aborting a long LLM stream while still
    // giving the AbortController a signal.
    const response = await this.request<Response>(url, {
      ...options,
      method: 'POST',
      body,
      skipJsonParse: true,
      skipLoading: true,
      timeout: 24 * 60 * 60 * 1000,
    })

    // request() already throws HttpError with .status on !response.ok, so we
    // only reach here if the server opened the stream successfully.

    const bodyStream: ReadableStream<Uint8Array> | null = response.body
    if (bodyStream === null) {
      const err = new Error('Erro ao iniciar stream: corpo de resposta ausente') as HttpError
      err.kind = 'stream-null-body'
      throw err
    }

    const reader = bodyStream.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Split on SSE double-newline delimiter and yield each complete frame.
        const parts = buffer.split('\n\n')
        // Keep the last (potentially incomplete) chunk in the buffer.
        buffer = parts.pop() ?? ''

        for (const part of parts) {
          const trimmed = part.trim()
          if (!trimmed) continue
          // Strip the leading `data: ` prefix from each SSE frame.
          const frame = trimmed.startsWith('data:') ? trimmed.slice(5).trimStart() : trimmed
          yield frame
        }
      }

      // Flush any remaining buffered text after the stream closes.
      const tail = decoder.decode()
      buffer += tail
      const remaining = buffer.split('\n\n')
      for (const part of remaining) {
        const trimmed = part.trim()
        if (!trimmed) continue
        const frame = trimmed.startsWith('data:') ? trimmed.slice(5).trimStart() : trimmed
        yield frame
      }
    } finally {
      reader.releaseLock()
    }
  }

  showError(message: string, error: Error | null = null): void {
    try {
      eventBus.emit('globalMessage.show', {
        type: 'error',
        title: 'Erro na Requisição',
        message,
        duration: 5000,
        error
      })
    } catch (eventError) {
      console.error('[HttpService] Failed to show error message:', eventError)
      console.error('[HttpService] Original error:', message, error)
    }
  }

  showSuccess(message: string): void {
    try {
      eventBus.emit('globalMessage.show', {
        type: 'success',
        message,
        duration: 3000
      })
    } catch (eventError) {
      console.error('[HttpService] Failed to show success message:', eventError)
    }
  }

  showInfo(message: string): void {
    try {
      eventBus.emit('globalMessage.show', {
        type: 'info',
        message,
        duration: 3000
      })
    } catch (eventError) {
      console.error('[HttpService] Failed to show info message:', eventError)
    }
  }

  async downloadFile(url: string, filename: string | null = null, options: RequestOptions = {}): Promise<boolean> {
    try {
      const response = await this.request<Response>(url, {
        ...options,
        skipJsonParse: true,
        logContext: options.logContext || 'HttpService.download'
      })

      const blob = await response.blob()

      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl

      if (filename) {
        link.download = filename
      } else {
        const contentDisposition = response.headers.get('Content-Disposition')
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (filenameMatch) {
            link.download = filenameMatch[1]
          }
        }
      }

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

      log(options.logContext || 'HttpService.download', 'success', 'File downloaded successfully', {
        url,
        filename: link.download
      })

      return true
    } catch (error) {
      log(options.logContext || 'HttpService.download', 'error', 'File download failed', error)
      throw error
    }
  }

  async uploadFile<T = unknown>(
    url: string,
    files: File | FileList | File[] | FormData,
    options: RequestOptions & { onProgress?: (progress: number) => void } = {}
  ): Promise<T> {
    const { onProgress: _onProgress, ...requestOptions } = options
    void _onProgress

    let formData: FormData

    if (files instanceof FormData) {
      formData = files
    } else {
      formData = new FormData()

      if (files instanceof FileList || Array.isArray(files)) {
        for (let i = 0; i < files.length; i++) {
          formData.append('files[]', files[i])
        }
      } else if (files instanceof File) {
        formData.append('file', files)
      } else {
        throw new Error('Invalid file parameter')
      }
    }

    return this.request<T>(url, {
      ...requestOptions,
      method: 'POST',
      body: formData,
      logContext: requestOptions.logContext || 'HttpService.upload'
    })
  }

  buildQueryOverrideHeaders(overrideParams: QueryOverrideParams): Record<string, string> {
    const { franquiaIds, dataInicio, dataFim, userIds } = overrideParams

    if (!franquiaIds || !franquiaIds.length) {
      throw new Error('franquiaIds is required for query override')
    }
    if (!dataInicio) {
      throw new Error('dataInicio is required for query override')
    }
    if (!dataFim) {
      throw new Error('dataFim is required for query override')
    }

    const headers: Record<string, string> = {
      'X-Query-Override': 'true',
      'X-Query-Franquias': Array.isArray(franquiaIds) ? franquiaIds.join(',') : String(franquiaIds),
      'X-Query-DataInicio': dataInicio,
      'X-Query-DataFim': dataFim
    }

    if (userIds && userIds.length) {
      headers['X-Query-UserIDs'] = Array.isArray(userIds) ? userIds.join(',') : String(userIds)
    }

    log('HttpService', 'buildQueryOverrideHeaders', 'Built override headers', headers)

    return headers
  }

  async getWithOverride<T = unknown>(url: string, overrideParams: QueryOverrideParams, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'GET',
      headers: {
        ...options.headers,
        ...this.buildQueryOverrideHeaders(overrideParams)
      }
    })
  }

  async postWithOverride<T = unknown>(url: string, data: unknown, overrideParams: QueryOverrideParams, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'POST',
      body: data,
      headers: {
        ...options.headers,
        ...this.buildQueryOverrideHeaders(overrideParams)
      }
    })
  }
}

export const httpService = new HttpService()
export { HttpService }

export const http = httpService
export default httpService
